import { useState, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../shared/components/ui/dropdown-menu';

type Category = {
  id: string;
  name: string;
  displayOrder: number;
};

type CategoryFilterDropdownProps = {
  categories: Category[];
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
  isLoading?: boolean;
};

export const CategoryFilterDropdown = ({
  categories,
  selectedCategoryIds,
  onChange,
  isLoading = false,
}: CategoryFilterDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Filter categories based on search term
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Sort by display order
  const sortedCategories = [...filteredCategories].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  const handleCategoryToggle = (categoryId: string) => {
    const isSelected = selectedCategoryIds.includes(categoryId);

    if (isSelected) {
      onChange(selectedCategoryIds.filter((id) => id !== categoryId));
    } else {
      onChange([...selectedCategoryIds, categoryId]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getButtonLabel = () => {
    if (isLoading) {
      return 'Loading...';
    }

    if (selectedCategoryIds.length === 0) {
      return 'Select categories';
    }

    return `${selectedCategoryIds.length} selected`;
  };

  const hasSelections = selectedCategoryIds.length > 0;

  return (
    <div>
      <label className="text-sm text-gray-700 mb-3 block">Category</label>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between text-sm text-gray-700"
            disabled={isLoading}
            aria-label="Category filter"
            aria-haspopup="menu"
          >
            <span className={selectedCategoryIds.length === 0 ? 'text-gray-500' : 'text-gray-700'}>
              {getButtonLabel()}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[280px] p-0"
          align="start"
          role="menu"
          onCloseAutoFocus={(e) => {
            // Prevent auto-focus on trigger when closing
            e.preventDefault();
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-9 text-sm"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                autoFocus={false}
              />
            </div>
          </div>

          {/* Clear All Button */}
          {hasSelections && (
            <>
              <div className="px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="w-full justify-start text-sm text-gray-600 hover:text-gray-900"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear All
                </Button>
              </div>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Category List */}
          <div className="max-h-[300px] overflow-y-auto">
            {categories.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No categories available
              </div>
            ) : sortedCategories.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No categories found
              </div>
            ) : (
              <div className="py-2 px-3 space-y-3">
                {sortedCategories.map((category) => (
                  <DropdownMenuCheckboxItem
                    key={category.id}
                    checked={selectedCategoryIds.includes(category.id)}
                    onCheckedChange={() => handleCategoryToggle(category.id)}
                    onSelect={(e) => {
                      // Prevent dropdown from closing on selection
                      e.preventDefault();
                    }}
                    className="cursor-pointer text-sm text-gray-700 flex items-center gap-2"
                  >
                    {category.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

